// Responsável e Retirada Pessoal (issue #17).
//
// Retirada Pessoal **não é uma Conta a Pagar** (ADR-0011). Se fosse, cairia no
// mesmo balde de despesas que este módulo existe para separar — o CONTEXT.md é
// explícito: "separando retiradas pessoais das despesas da empresa".
//
// Por isso não tem vencimento nem Pagamento: retirada já é dinheiro que saiu.
// O limite mensal do Responsável **avisa, nunca bloqueia**.

import { newId, type Id } from "./ids.ts";
import { isDataISO, type DataISO } from "./datas.ts";
import type { Centavos } from "./money.ts";
import type { Responsavel, RetiradaPessoal, Snapshot } from "./snapshot.ts";

export type { Responsavel, RetiradaPessoal };

export class ErroResponsavel extends Error {}
export class ErroRetirada extends Error {}

export interface DadosResponsavel {
  nome: string;
  /** `null` = sem limite. */
  limiteMensalCentavos: Centavos | null;
}

export interface DadosRetirada {
  responsavelId: Id;
  data: DataISO;
  valorCentavos: Centavos;
}

// --- Responsável -----------------------------------------------------------

function validarResponsavel(
  dados: DadosResponsavel,
  snapshot: Snapshot,
  ignorarId?: Id,
): Omit<Responsavel, "id" | "ativo"> {
  const nome = dados.nome.trim();
  if (nome === "") throw new ErroResponsavel("Informe o nome do responsável.");
  if (
    snapshot.responsaveis.some(
      (r) => r.id !== ignorarId && r.nome.toLowerCase() === nome.toLowerCase(),
    )
  ) {
    throw new ErroResponsavel(`Já existe o responsável "${nome}".`);
  }
  const limite = dados.limiteMensalCentavos;
  if (limite != null && (!Number.isInteger(limite) || limite < 0)) {
    throw new ErroResponsavel("Limite mensal inválido.");
  }
  return { nome, limiteMensalCentavos: limite };
}

export function criarResponsavel(
  snapshot: Snapshot,
  dados: DadosResponsavel,
  id: Id = newId(),
): Snapshot {
  const r: Responsavel = {
    id,
    ...validarResponsavel(dados, snapshot),
    ativo: true,
  };
  return { ...snapshot, responsaveis: [...snapshot.responsaveis, r] };
}

export function editarResponsavel(
  snapshot: Snapshot,
  id: Id,
  dados: DadosResponsavel,
): Snapshot {
  if (!snapshot.responsaveis.some((r) => r.id === id)) {
    throw new ErroResponsavel("Responsável não encontrado.");
  }
  const campos = validarResponsavel(dados, snapshot, id);
  return {
    ...snapshot,
    responsaveis: snapshot.responsaveis.map((r) =>
      r.id === id ? { ...r, ...campos } : r,
    ),
  };
}

export function definirAtivoResponsavel(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  if (!snapshot.responsaveis.some((r) => r.id === id)) {
    throw new ErroResponsavel("Responsável não encontrado.");
  }
  return {
    ...snapshot,
    responsaveis: snapshot.responsaveis.map((r) => (r.id === id ? { ...r, ativo } : r)),
  };
}

// --- Retirada Pessoal ------------------------------------------------------

export function registrarRetirada(
  snapshot: Snapshot,
  dados: DadosRetirada,
  id: Id = newId(),
): Snapshot {
  const resp = snapshot.responsaveis.find((r) => r.id === dados.responsavelId);
  if (!resp) throw new ErroRetirada("Escolha o responsável pela retirada.");
  if (!resp.ativo) {
    throw new ErroRetirada(`${resp.nome} está inativo e não pode retirar.`);
  }
  if (!isDataISO(dados.data)) throw new ErroRetirada("Informe a data da retirada.");
  if (!Number.isInteger(dados.valorCentavos) || dados.valorCentavos <= 0) {
    throw new ErroRetirada("Informe um valor maior que zero.");
  }
  const r: RetiradaPessoal = {
    id,
    responsavelId: dados.responsavelId,
    data: dados.data,
    valorCentavos: dados.valorCentavos,
  };
  return { ...snapshot, retiradasPessoais: [...snapshot.retiradasPessoais, r] };
}

export function editarRetirada(
  snapshot: Snapshot,
  id: Id,
  dados: DadosRetirada,
): Snapshot {
  if (!snapshot.retiradasPessoais.some((r) => r.id === id)) {
    throw new ErroRetirada("Retirada não encontrada.");
  }
  // reaproveita as mesmas guardas do registro
  const sem = excluirRetirada(snapshot, id);
  return registrarRetirada(sem, dados, id);
}

export function excluirRetirada(snapshot: Snapshot, id: Id): Snapshot {
  if (!snapshot.retiradasPessoais.some((r) => r.id === id)) {
    throw new ErroRetirada("Retirada não encontrada.");
  }
  return {
    ...snapshot,
    retiradasPessoais: snapshot.retiradasPessoais.filter((r) => r.id !== id),
  };
}

// --- consultas -------------------------------------------------------------

/** `AAAA-MM` de uma data ISO. */
export function mesDe(data: DataISO): string {
  return data.slice(0, 7);
}

/** Quanto um Responsável retirou num mês (`AAAA-MM`). */
export function retiradoNoMes(
  snapshot: Snapshot,
  responsavelId: Id,
  mes: string,
): Centavos {
  return snapshot.retiradasPessoais
    .filter((r) => r.responsavelId === responsavelId && mesDe(r.data) === mes)
    .reduce((soma, r) => soma + r.valorCentavos, 0);
}

export interface SituacaoLimite {
  retirado: Centavos;
  limite: Centavos | null;
  /** Passou do limite? Sempre `false` quando não há limite. */
  estourou: boolean;
}

/**
 * Quanto o Responsável já retirou no mês, contra o limite dele.
 *
 * É informativo: nada no domínio bloqueia por causa disto. O limite serve para
 * a tela avisar, e a decisão continua sendo de quem opera.
 */
export function situacaoLimite(
  snapshot: Snapshot,
  responsavelId: Id,
  mes: string,
): SituacaoLimite {
  const resp = snapshot.responsaveis.find((r) => r.id === responsavelId);
  const retirado = retiradoNoMes(snapshot, responsavelId, mes);
  const limite = resp?.limiteMensalCentavos ?? null;
  return { retirado, limite, estourou: limite != null && retirado > limite };
}

/** Total retirado por todos num mês — o número que fica à parte das despesas. */
export function totalRetiradoNoMes(snapshot: Snapshot, mes: string): Centavos {
  return snapshot.retiradasPessoais
    .filter((r) => mesDe(r.data) === mes)
    .reduce((soma, r) => soma + r.valorCentavos, 0);
}
